import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { getDomains, addDomain, removeDomain, toggleDomainActive } from '../utils/domains';

const { FiSettings, FiPlus, FiTrash2, FiToggleLeft, FiToggleRight, FiInfo, FiExternalLink, FiCopy, FiCheck } = FiIcons;

const AdminPanel = ({ isOpen, onClose }) => {
  const [domains, setDomains] = useState(getDomains());
  const [newDomain, setNewDomain] = useState('');
  const [newDomainName, setNewDomainName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copied, setCopied] = useState('');

  const handleAddDomain = (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!newDomain.trim()) {
      setError('Please enter a domain');
      return;
    }

    try {
      const updatedDomains = addDomain(newDomain.trim(), newDomainName.trim());
      setDomains(updatedDomains);
      setNewDomain('');
      setNewDomainName('');
      setSuccess('Domain added successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to add domain');
    }
  };

  const handleRemoveDomain = (domainId) => {
    try {
      const updatedDomains = removeDomain(domainId);
      setDomains(updatedDomains);
      setSuccess('Domain removed successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to remove domain');
    }
  };

  const handleToggleActive = (domainId) => {
    try {
      const updatedDomains = toggleDomainActive(domainId);
      setDomains(updatedDomains);
    } catch (err) {
      setError('Failed to toggle domain status');
    }
  };

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(id);
      setTimeout(() => setCopied(''), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard');
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-4xl max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiSettings} className="text-2xl" />
                <h2 className="text-2xl font-bold">Admin Panel</h2>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 text-2xl"
              >
                ×
              </button>
            </div>
          </div>

          <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
            {/* Domain Management */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Domain Management</h3>
              
              {/* Add New Domain */}
              <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <h4 className="font-semibold text-gray-700 mb-4">Add New Domain</h4>
                <form onSubmit={handleAddDomain} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Domain
                      </label>
                      <input
                        type="text"
                        value={newDomain}
                        onChange={(e) => setNewDomain(e.target.value)}
                        placeholder="short.example.com"
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Display Name (Optional)
                      </label>
                      <input
                        type="text"
                        value={newDomainName}
                        onChange={(e) => setNewDomainName(e.target.value)}
                        placeholder="Short Links"
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200 flex items-center space-x-2"
                  >
                    <SafeIcon icon={FiPlus} />
                    <span>Add Domain</span>
                  </button>
                </form>
              </div>

              {/* Current Domains */}
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-700">Current Domains</h4>
                {domains.map((domain) => (
                  <div
                    key={domain.id}
                    className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between"
                  >
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h5 className="font-semibold text-gray-800">{domain.name}</h5>
                        {domain.primary && (
                          <span className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full text-xs font-semibold">
                            Primary
                          </span>
                        )}
                        {!domain.active && (
                          <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs font-semibold">
                            Inactive
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className="text-gray-600 font-mono text-sm">{domain.domain}</span>
                        <button
                          onClick={() => copyToClipboard(domain.domain, domain.id)}
                          className="text-gray-400 hover:text-emerald-600 transition-colors"
                        >
                          <SafeIcon icon={copied === domain.id ? FiCheck : FiCopy} className="text-sm" />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleToggleActive(domain.id)}
                        className={`p-2 rounded-lg transition-colors ${
                          domain.active 
                            ? 'text-emerald-600 hover:bg-emerald-50' 
                            : 'text-gray-400 hover:bg-gray-50'
                        }`}
                        title={domain.active ? 'Deactivate' : 'Activate'}
                      >
                        <SafeIcon icon={domain.active ? FiToggleRight : FiToggleLeft} className="text-xl" />
                      </button>
                      {!domain.primary && (
                        <button
                          onClick={() => handleRemoveDomain(domain.id)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title="Remove Domain"
                        >
                          <SafeIcon icon={FiTrash2} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Setup Instructions */}
            <div className="bg-blue-50 rounded-xl p-6">
              <div className="flex items-start space-x-3">
                <SafeIcon icon={FiInfo} className="text-blue-500 mt-1" />
                <div>
                  <h4 className="font-semibold text-blue-800 mb-3">Domain Setup Instructions</h4>
                  <div className="text-blue-700 text-sm space-y-3">
                    <div>
                      <h5 className="font-semibold mb-2">To add a new subdomain or domain:</h5>
                      <ol className="list-decimal list-inside space-y-1 ml-4">
                        <li>Purchase/configure your domain with your DNS provider</li>
                        <li>Create a CNAME record pointing to your main site (linker.nurulquran.com)</li>
                        <li>Add the domain in the form above</li>
                        <li>Test the domain to ensure it's working correctly</li>
                      </ol>
                    </div>
                    
                    <div>
                      <h5 className="font-semibold mb-2">DNS Configuration Example:</h5>
                      <div className="bg-white p-3 rounded border font-mono text-xs">
                        <div>Type: CNAME</div>
                        <div>Name: short (for short.example.com)</div>
                        <div>Value: linker.nurulquran.com</div>
                      </div>
                    </div>

                    <div>
                      <h5 className="font-semibold mb-2">Deployment Configuration:</h5>
                      <p className="mb-2">Update your hosting platform's redirect rules to include the new domain:</p>
                      <div className="bg-white p-3 rounded border font-mono text-xs">
                        <div># Netlify _redirects</div>
                        <div>/r/:shortCode /#/r/:shortCode 200</div>
                        <div>/* /index.html 200</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Status Messages */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg"
              >
                {error}
              </motion.div>
            )}

            {success && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg"
              >
                {success}
              </motion.div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AdminPanel;